import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Coins } from 'lucide-react'
import { toast } from 'sonner'
import { DashboardLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { getAiCalls, getAiUsage, updateCreditModel, updateFxRate } from '@/lib/api'
import { atsNav } from '@/lib/atsNav'
import { PageHeader } from '@/lib/atsUi'
import { currentTenant, currentUser } from '@/lib/mocks'

export const Route = createFileRoute('/ai-usage')({ component: AiUsagePage })

const num = (n: number) => n.toLocaleString('en-IN')
const usd = (n: number) => `$${n < 1 ? n.toFixed(4) : n.toFixed(2)}`
const inr = (n: number) => `₹${n.toFixed(2)}`
// Credits can be fractional (0.5/record). Show integers plainly, fractions to 1–2 dp.
const cred = (n: number) => (Number.isInteger(n) ? num(n) : n < 1 ? n.toFixed(2) : n.toFixed(1))
const fmtTime = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
// Human-readable activity names for the raw ai_call_log task codes.
const TASK_LABELS: Record<string, string> = {
  parse_cv: 'CV parse',
  parse_cv_fields: 'CV field extraction',
  parse_jd: 'JD parse',
  rerank: 'Candidate re-rank',
  classify_status: 'Status classify',
  taxonomy: 'Skill taxonomy',
}
const taskLabel = (t: string) => TASK_LABELS[t] ?? t.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())
const PAGE_SIZES = [5, 10, 25]

function AiUsagePage() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['ai-usage'], queryFn: getAiUsage, refetchInterval: 5000 })
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(5)
  const { data: calls } = useQuery({ queryKey: ['ai-calls', page, pageSize], queryFn: () => getAiCalls(pageSize, page * pageSize), placeholderData: (prev) => prev })
  const [rate, setRate] = useState('')
  useEffect(() => {
    if (data?.fxRate != null && rate === '') setRate(String(data.fxRate))
  }, [data?.fxRate, rate])

  const saveRate = async () => {
    const r = Number(rate)
    if (!Number.isFinite(r) || r <= 0) {
      toast.error('Enter a valid rate')
      return
    }
    await updateFxRate(r)
    qc.invalidateQueries({ queryKey: ['ai-usage'] })
    toast.success(`USD→INR rate set to ₹${r}`)
  }

  // Credit model: ₹/credit, credits charged per record, and the target markup over internal cost.
  const c = data?.credits
  const [creditInr, setCreditInr] = useState('')
  const [baseRate, setBaseRate] = useState('')
  const [markup, setMarkup] = useState('')
  useEffect(() => {
    if (!c) return
    setCreditInr((p) => (p === '' ? String(c.inrPerCredit) : p))
    setBaseRate((p) => (p === '' ? String(c.baseCreditsPerRecord) : p))
    setMarkup((p) => (p === '' ? String(c.targetMarkup) : p))
  }, [c])

  const saveCredits = async () => {
    const ipc = Number(creditInr)
    const base = Number(baseRate)
    const tm = Number(markup)
    if (!Number.isFinite(ipc) || ipc <= 0) return toast.error('₹ per credit must be positive')
    if (!Number.isFinite(base) || base < 0) return toast.error('Credits/record must be ≥ 0')
    if (!Number.isFinite(tm) || tm <= 0) return toast.error('Target markup must be positive')
    await updateCreditModel({ inrPerCredit: ipc, baseCreditsPerRecord: base, targetMarkup: tm })
    qc.invalidateQueries({ queryKey: ['ai-usage'] })
    qc.invalidateQueries({ queryKey: ['ai-calls'] })
    toast.success(`Credits: ${cred(base)}/record · 1 credit = ₹${ipc}`)
  }
  // Implied markup is "on target" when within ±20% of the configured target.
  const onTarget = c?.impliedMarkup != null && Math.abs(c.impliedMarkup - c.targetMarkup) <= c.targetMarkup * 0.2

  return (
    <DashboardLayout title="AI Usage & Cost" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader
        title="AI Usage & Cost — Super Admin"
        subtitle="Token consumption, model pricing (USD), and live ₹ cost. The internal ledger behind any client pricing."
      />

      {/* FX rate */}
      <Card className="mb-6">
        <CardContent className="flex flex-wrap items-end gap-4 pt-5 pb-5">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">USD → INR rate (set daily)</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">$1 =</span>
              <input
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="w-24 rounded-md border border-input bg-card px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <span className="text-sm text-muted-foreground">₹</span>
              <Button size="sm" onClick={saveRate}>
                Update
              </Button>
            </div>
          </div>
          <div className="ml-auto grid grid-cols-2 gap-6 sm:grid-cols-4">
            <Stat label="AI calls" value={num(data?.totals.calls ?? 0)} />
            <Stat label="Tokens" value={num((data?.totals.inputTokens ?? 0) + (data?.totals.outputTokens ?? 0))} />
            <Stat label="Per screening" value={inr(data?.perJob.inr ?? 0)} />
            <Stat label="Cost to date" value={inr(data?.totals.inr ?? 0)} accent />
          </div>
        </CardContent>
      </Card>

      {/* Credits & client pricing */}
      <Card className="mb-6">
        <CardContent className="pt-5 pb-5">
          <div className="mb-4 flex items-center gap-2">
            <Coins className="h-4 w-4 text-primary" />
            <div className="text-sm font-medium">Credits & client pricing</div>
            <span className="text-xs text-muted-foreground">— what clients are billed per record scanned</span>
          </div>
          <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
            <CreditField label="₹ per credit" prefix="₹" value={creditInr} onChange={setCreditInr} />
            <CreditField label="Credits / record" value={baseRate} onChange={setBaseRate} />
            <CreditField label="Target markup" suffix="×" value={markup} onChange={setMarkup} />
            <Button size="sm" onClick={saveCredits}>Update</Button>

            <div className="ml-auto grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
              <Stat label="Per record" value={c ? `${cred(c.perRecordCredits)} cr · ${inr(c.perRecordInr)}` : '—'} />
              <Stat label="Implied markup" value={c?.impliedMarkup != null ? `${c.impliedMarkup.toFixed(1)}×` : '—'} accent={onTarget} />
              <Stat label="Credits billed" value={cred(c?.creditsToDate ?? 0)} />
              <Stat label="Credit revenue" value={inr(c?.revenueInrToDate ?? 0)} accent />
            </div>
          </div>
          {c && (
            <div className="mt-3 text-xs text-muted-foreground">
              1 credit = ₹{c.inrPerCredit}. At {cred(c.baseCreditsPerRecord)} credits/record → 10 records = {cred(10 * c.baseCreditsPerRecord)} credits,
              {' '}1,000 records = {cred(1000 * c.baseCreditsPerRecord)} credits.{' '}
              <span className="text-foreground">
                To hit {c.targetMarkup}× internal cost ({data?.basis === 'measured' ? 'measured' : 'estimated'} ~{inr(data?.perRecord.inr ?? 0)}/record), charge ~{cred(c.suggestedCreditsPerRecord)} credits/record.
              </span>{' '}
              <button type="button" className="font-medium text-primary hover:underline" onClick={() => setBaseRate(String(c.suggestedCreditsPerRecord))}>
                Apply suggestion
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-model ledger */}
      <Card className="mb-6">
        <CardContent className="p-0">
          <div className="border-b border-border px-4 py-3 text-sm font-medium">Consumption by model</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Model</th>
                  <th className="px-4 py-2 text-right font-medium">Calls</th>
                  <th className="px-4 py-2 text-right font-medium">Input tok</th>
                  <th className="px-4 py-2 text-right font-medium">Output tok</th>
                  <th className="px-4 py-2 text-right font-medium">$/1M (in/out)</th>
                  <th className="px-4 py-2 text-right font-medium">Cost (USD)</th>
                  <th className="px-4 py-2 text-right font-medium">Cost (₹)</th>
                </tr>
              </thead>
              <tbody>
                {(data?.byModel ?? []).map((m) => (
                  <tr key={m.model} className="border-b border-border last:border-0">
                    <td className="px-4 py-2">
                      <div className="font-medium">{m.model}</div>
                      <div className="text-xs text-muted-foreground">{m.provider} · {m.avgLatencyMs}ms avg</div>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{num(m.calls)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{num(m.inputTokens)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{num(m.outputTokens)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                      {m.inputUsdPerM != null ? `$${m.inputUsdPerM}/$${m.outputUsdPerM}` : '—'}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{usd(m.usd)}</td>
                    <td className="px-4 py-2 text-right font-medium tabular-nums">{inr(m.inr)}</td>
                  </tr>
                ))}
                {data && (
                  <tr className="bg-muted/30 font-medium">
                    <td className="px-4 py-2">Total</td>
                    <td className="px-4 py-2 text-right tabular-nums">{num(data.totals.calls)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{num(data.totals.inputTokens)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{num(data.totals.outputTokens)}</td>
                    <td className="px-4 py-2" />
                    <td className="px-4 py-2 text-right tabular-nums">{usd(data.totals.usd)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{inr(data.totals.inr)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Projection */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b border-border px-4 py-3 text-sm font-medium">
            What clients pay to scan candidates{' '}
            <span className="text-xs text-muted-foreground">
              (credits billed · {data?.basis === 'measured'
                ? `internal cost measured from real usage — ~${(data.perRecord.inputTokens + data.perRecord.outputTokens)} tok/record`
                : 'internal cost estimated'})
            </span>
          </div>
          <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-5">
            {(data?.projection ?? []).map((p) => (
              <div key={p.records} className="bg-card px-4 py-4 text-center">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{num(p.records)} records</div>
                <div className="mt-1 text-lg font-semibold tabular-nums">
                  {cred(p.credits)} <span className="text-xs font-normal text-muted-foreground">cr</span>
                </div>
                <div className="text-sm tabular-nums text-foreground">{inr(p.creditInr)}</div>
                <div className="text-xs text-muted-foreground">cost {inr(p.inr)}</div>
              </div>
            ))}
          </div>
          <div className="px-4 py-3 text-xs text-muted-foreground">
            Credits are what the client is billed (1 credit = ₹{c?.inrPerCredit ?? 1}); "cost" is our internal spend. The gap is the markup
            {c ? ` (target ${c.targetMarkup}×)` : ''}. Per-record credits are the lever — raise them for heavier/more-complex models.
          </div>
        </CardContent>
      </Card>

      {/* Full per-call API ledger */}
      <Card className="mt-6">
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div className="text-sm font-medium">API call log <span className="text-xs font-normal text-muted-foreground">— bundled by trigger, newest first</span></div>
            {calls && calls.total > 0 && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Rows</span>
                <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0) }} className="h-7 rounded-md border border-input bg-card px-1.5 focus:outline-none focus:ring-2 focus:ring-ring" aria-label="Rows per page">
                  {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <span className="tabular-nums">{page * pageSize + 1}–{Math.min((page + 1) * pageSize, calls.total)} of {num(calls.total)}</span>
                <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="rounded border border-input px-2 py-1 hover:bg-muted/50 disabled:opacity-40">Prev</button>
                <button disabled={(page + 1) * pageSize >= calls.total} onClick={() => setPage((p) => p + 1)} className="rounded border border-input px-2 py-1 hover:bg-muted/50 disabled:opacity-40">Next</button>
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Time</th>
                  <th className="px-4 py-2 font-medium">Trigger</th>
                  <th className="px-4 py-2 font-medium">Job ref</th>
                  <th className="px-4 py-2 font-medium">Model</th>
                  <th className="px-4 py-2 text-right font-medium">Records</th>
                  <th className="px-4 py-2 text-right font-medium">Tokens (in/out)</th>
                  <th className="px-4 py-2 text-right font-medium" title="Average per call when bundled">Latency</th>
                  <th className="px-4 py-2 text-right font-medium" title="Credits billed to the client = records × per-record rate">Credits</th>
                  <th className="px-4 py-2 text-right font-medium" title="Our internal spend on this trigger">Cost (₹)</th>
                </tr>
              </thead>
              <tbody>
                {(calls?.rows ?? []).map((c) => {
                  const bundled = c.callCount > 1
                  return (
                  <tr key={c.id} className="border-b border-border last:border-0">
                    <td className="whitespace-nowrap px-4 py-2 text-muted-foreground">{fmtTime(c.createdAt)}</td>
                    <td className="px-4 py-2">
                      <span title={c.task} className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">{taskLabel(c.task)}</span>
                      {bundled && <span title={`${c.callCount} per-record API calls bundled into one trigger`} className="ml-1.5 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">×{c.callCount} calls</span>}
                    </td>
                    <td className="max-w-[220px] truncate px-4 py-2 text-muted-foreground" title={c.jobRef ?? undefined}>{c.jobRef ?? '—'}</td>
                    <td className="px-4 py-2"><span className="text-foreground">{c.model}</span> <span className="text-xs text-muted-foreground">· {c.provider ?? '—'}{c.cacheHit ? ' · cached' : ''}</span></td>
                    <td className="px-4 py-2 text-right tabular-nums">{c.records != null ? <span className="font-medium">{num(c.records)}</span> : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{num(c.inputTokens)}/{num(c.outputTokens)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{c.latencyMs != null ? `${num(c.latencyMs)}ms${bundled ? ' avg' : ''}` : '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {c.credits > 0 ? <span className="font-medium">{cred(c.credits)} <span className="text-xs font-normal text-muted-foreground">· {inr(c.creditInr)}</span></span> : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{inr(c.inr)}</td>
                  </tr>
                  )
                })}
                {calls && calls.rows.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">No AI calls logged yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  )
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${accent ? 'text-success' : ''}`}>{value}</div>
    </div>
  )
}

function CreditField({
  label,
  value,
  onChange,
  prefix,
  suffix,
}: { label: string; value: string; onChange: (v: string) => void; prefix?: string; suffix?: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-center gap-1.5">
        {prefix && <span className="text-sm text-muted-foreground">{prefix}</span>}
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode="decimal"
          className="w-20 rounded-md border border-input bg-card px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  )
}
